import { EntityType } from '../../../models/enums';
import { AlignDirection, CellTemplateType, FilterTemplateType, GenericColDef, SortByFilter } from '../../../models/grid-context';
import { ValueGetterParams } from 'ag-grid-community';
import { GridCellToggleSwitchComponent } from '../../grid-cell-toggle-switch/grid-cell-toggle-switch.component';

enum SummaryColumnType {
    checkbox = 'checkbox',
    contextMenu = 'contextMenu',
    description = 'description',
    lastUpdateTimestamp = 'lastUpdateTimestamp',
    documents = 'documents',
    samples = 'samples',
    createdOn = 'createdOn',
    dateCreated = 'dateCreated',
    dateCreatedSortable = 'dateCreatedSortable',
    // folder
    folderName = 'folderName',
    // tag
    tagName = 'tagName',
    // custodian
    custodianName = 'custodianName',
    priorityDisplay = 'priority',
    documentsSortable = 'documentsSortable',
    //mediaid
    mediaIDName = 'mediaIDName',
    //batch
    batchViewName = 'batchViewName',
    // import
    dataSetName = 'dataSetName',
    representationType = 'representationType',
    inCaseData = 'inCaseData',
    connectorName = 'connectorName',
    dataAreaPaths = 'dataAreaPaths',
    shared = 'shared',
    originatingProject = 'originatingProject',
    batchName = 'batchName',
    // export
    exportName = 'exportName',
    exportState = 'exportState',
    exportedBy = 'exportedBy',
    // saved search
    savedSearchName = 'savedSearchName',
    searchTask = 'searchTask',
    createdBy = 'createdBy',
    // search
    searchName = 'searchName',
    results = 'results',
    owner = 'owner',
    started = 'started',
    finished = 'finished',
    duration = 'duration',
    task = 'task',
    // analytic profile
    profileName = 'profileName',
    profileText = 'profileText',
    dateAdded = 'dateAdded',
    user = 'user',
    dateModified = 'dateModified',
    profileComment = 'profileComment',
    // analytic comparison
    comparisonName = 'comparisonName',
    documentSetA = 'documentSetA',
    documentSetB = 'documentSetB',
    // cluster
    clusterDrill = 'clusterDrill',
    clusterId = 'clusterId',
    clusterName = 'clusterName',
    clusterTopTerms = 'clusterTopTerms',
    clusteredTopTerms = 'clusteredTopTerms',
    clusterDocuments = 'clusterDocuments',
    clusterTags = 'clusterTags',
    // workflows
    name = 'name',
    locked = 'locked',
    modifiedBy = 'modifiedBy',
    workflowCreatedBy = 'workflowCreatedBy',
    dateLastExecuted = 'lastExecutedOn',
    lastExecutedBy = 'lastExecutedBy'
}

const allColumnDefs: ReadonlyMap<SummaryColumnType, (hide: boolean) => GenericColDef> = new Map<SummaryColumnType, (hide: boolean) => GenericColDef>([
    [SummaryColumnType.checkbox, (hide: boolean) => <GenericColDef>{
        colId: 'checkbox', automationId: '',
        checkboxSelection: true, headerCheckboxSelection: true,
        minWidth: 36, maxWidth: 36, width: 36,
        suppressSizeToFit: true, suppressMovable: true,
        hide: hide
    }],
    [SummaryColumnType.contextMenu, (hide: boolean) => <GenericColDef>{
        cellTemplateType: CellTemplateType.contextMenu, automationId: '',
        minWidth: 30, maxWidth: 30, width: 30,
        suppressSizeToFit: true,
        suppressMovable: true,
        hide: hide
    }],
    [SummaryColumnType.dataSetName, (hide: boolean) => <GenericColDef>{
        headerName: 'Data Set Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        lockVisible: true,
        hide: hide
    }],
    [SummaryColumnType.shared, (hide: boolean) => <GenericColDef>{
        headerName: 'Shared',automationId: '',
        cellRenderer: GridCellToggleSwitchComponent,
        width: 70, minWidth: 70, maxWidth: 70,
        cellRendererParams: {
            isToggleBox: true,
            marginLeft: '15',
            isSkeuomorphic: true
        },
        cellAlignDirection: AlignDirection.center,
        valueGetter: function tokenNameGetter(params) {
            const isShared = params.data.sharedState === 'PRIVATE' ? false : true;
            return isShared;
        }
    }],
    [SummaryColumnType.folderName, (hide: boolean) => <GenericColDef>{
        headerName: 'Folder Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        lockVisible: true,
        hide: hide,
        filterType: FilterTemplateType.freeform,
        serviceTierFilter: true,
        suppressMovable: true
    }],
    [SummaryColumnType.tagName, (hide: boolean) => <GenericColDef>{
        headerName: 'Tag Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        lockVisible: true,
        hide: hide,
        filterType: FilterTemplateType.freeform,
        serviceTierFilter: true,
        suppressMovable: true
    }],
    [SummaryColumnType.custodianName, (hide: boolean) => <GenericColDef>{
        headerName: 'Custodian Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        lockVisible: true,
        filterType: FilterTemplateType.freeform,
        suppressMovable: true,
        hide: hide,
        serviceTierFilter: true
    }],
    [SummaryColumnType.exportName, (hide: boolean) => <GenericColDef>{
        headerName: 'Export Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        lockVisible: true,
        suppressMovable: true,
        hide: hide
    }],
    [SummaryColumnType.representationType, (hide: boolean) => <GenericColDef>{
        headerName: 'Index Level', field: 'representationTypeDisplay', automationId: '',
        width: 120,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.exportState, (hide: boolean) => <GenericColDef>{
        headerName: 'Production State', field: 'stateDisplay', automationId: '',
        width: 120,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.createdOn, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Created', field: 'createdOn', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.dateCreated, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Created', field: 'dateCreated', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.dateCreatedSortable, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Created', field: 'dateCreated', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide,
        sortBy: SortByFilter.DATE_CREATED
    }],
    [SummaryColumnType.lastUpdateTimestamp, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Created', field: 'lastUpdateTimestamp', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 140,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.priorityDisplay, (hide: boolean) => <GenericColDef>{
        headerName: 'Priority', field: 'priorityDisplay', automationId: '',
        width: 65,
        suppressSizeToFit: true,
        sortBy: SortByFilter.SIZE,
        hide: hide
    }],
    [SummaryColumnType.documents, (hide: boolean) => <GenericColDef>{
        headerName: 'Documents', field: 'documentCount', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 120,
        hide: hide
    }],
    [SummaryColumnType.documentsSortable, (hide: boolean) => <GenericColDef>{
        headerName: 'Documents', field: 'documentCount', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 120,
        sortBy: SortByFilter.COUNT,
        hide: hide
    }],
    [SummaryColumnType.samples, (hide: boolean) => <GenericColDef>{
        headerName: 'Samples', field: 'sampleSetCount', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 100,
        hide: hide
    }],
    [SummaryColumnType.inCaseData, (hide: boolean) => <GenericColDef>{
        headerName: 'In Project Data', field: 'inCaseData', automationId: '',
        cellTemplateType: CellTemplateType.iconSwitch,
        cellRendererParams: { iconOnClass: 'fa-check color-grey-10' },
        headerAlignDirection: AlignDirection.center,
        width: 120,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.connectorName, (hide: boolean) => <GenericColDef>{
        headerName: 'Connector Name', field: 'connectorName', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.dataAreaPaths, (hide: boolean) => <GenericColDef>{
        headerName: 'Data Areas', field: 'dataAreaPaths', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.originatingProject, (hide: boolean) => <GenericColDef>{
        headerName: 'Originating Project', field: 'ownerName', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.description, (hide: boolean) => <GenericColDef>{
        headerName: 'Description', field: 'description', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.batchName, (hide: boolean) => <GenericColDef>{
        headerName: 'Batch', field: 'batchName', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.searchName, (hide: boolean) => <GenericColDef>{
        headerName: 'Search Task', field: 'name', automationId: '',
        filterType: FilterTemplateType.freeform, suppressMovable: true,
        valueGetter: (params: ValueGetterParams) => (params && params.data && params.data.name) ? params.data.name.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
        lockVisible: true,
        hide: hide,
        serviceTierFilter: true
    }],
    [SummaryColumnType.results, (hide: boolean) => <GenericColDef>{
        headerName: 'Results', field: 'documentCount', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        hide: hide
    }],
    [SummaryColumnType.owner, (hide: boolean) => <GenericColDef>{
        headerName: 'Owner', field: 'owner', automationId: '',
        filterType: FilterTemplateType.ownerSingleChoice,
        hide: hide,
        serviceTierFilter: true
    }],
    [SummaryColumnType.started, (hide: boolean) => <GenericColDef>{
        headerName: 'Started', field: 'dateStarted', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.finished, (hide: boolean) => <GenericColDef>{
        headerName: 'Finished', field: 'dateCompleted', automationId: '',
        cellTemplateType: CellTemplateType.date,
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.duration, (hide: boolean) => <GenericColDef>{
        headerName: 'Duration', field: 'secondsElapsed', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        hide: hide
    }],
    [SummaryColumnType.task, (hide: boolean) => <GenericColDef>{
        headerName: 'Task Code', field: 'task', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.profileName, (hide: boolean) => <GenericColDef>{
        headerName: 'Synthetic Document Name', field: 'name',
        automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        hide: hide
    }],
    [SummaryColumnType.dateAdded, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Added', field: 'createdOn', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellTemplateType: CellTemplateType.date,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.user, (hide: boolean) => <GenericColDef>{
        headerName: 'User', field: 'modifiedBy', automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        hide: hide
    }],
    [SummaryColumnType.exportedBy, (hide: boolean) => <GenericColDef>{
        headerName: 'Exported by', field: 'createdBy', automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        hide: hide
    }],
    [SummaryColumnType.dateModified, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Modified', field: 'modifiedOn', automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellTemplateType: CellTemplateType.date,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.profileComment, (hide: boolean) => <GenericColDef>{
        headerName: 'Comment', field: 'comments', automationId: '',
        cellTemplateType: CellTemplateType.nameIconLabel,
        hide: hide
    }],
    [SummaryColumnType.comparisonName, (hide: boolean) => <GenericColDef>{
        headerName: 'Comparison Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        hide: hide
    }],
    [SummaryColumnType.documentSetA, (hide: boolean) => <GenericColDef>{
        headerName: 'Document Set A', field: 'viewA.name', automationId: '',
        valueGetter: (params: ValueGetterParams) => (params && params.data && params.data.viewA && params.data.viewA.name) ? params.data.viewA.name.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
        cellTemplateType: CellTemplateType.nameIconLabel, alternativeFieldNames: { iconClass: 'iconClassDocumentSetA', iconColor: 'iconColorDocumentSetA' },
        hide: hide
    }],
    [SummaryColumnType.documentSetB, (hide: boolean) => <GenericColDef>{
        headerName: 'Document Set B', field: 'viewB.name', automationId: '',
        valueGetter: (params: ValueGetterParams) => (params && params.data && params.data.viewB && params.data.viewB.name) ? params.data.viewB.name.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
        cellTemplateType: CellTemplateType.nameIconLabel, alternativeFieldNames: { iconClass: 'iconClassDocumentSetB', iconColor: 'iconColorDocumentSetB' },
        hide: hide
    }],
    [SummaryColumnType.clusterDrill, (hide: boolean) => <GenericColDef>{
        cellTemplateType: CellTemplateType.iconCluster,
        minWidth: 30, maxWidth: 30, width: 30,
        suppressSizeToFit: true,
        suppressMovable: true,
        hide: hide,
        automationId: ''
    }],
    [SummaryColumnType.clusterId, (hide: boolean) => <GenericColDef>{
        headerName: 'ID', field: 'clusterID',
        headerAlignDirection: AlignDirection.left,
        hide: hide, suppressSizeToFit: true,
        width: 80, lockVisible: true,
        automationId: ''
    }],
    [SummaryColumnType.clusterName, (hide: boolean) => <GenericColDef>{
        headerName: 'Name', field: 'name',
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        hide: hide,
        automationId: ''
    }],
    [SummaryColumnType.clusteredTopTerms, (hide: boolean) => <GenericColDef>{
        headerName: 'Top Terms', field: 'topTerms',
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        hide: hide,
        cellTemplateType: CellTemplateType.clusteredTopTerms,
        automationId: ''
    }],
    [SummaryColumnType.clusterTopTerms, (hide: boolean) => <GenericColDef>{
        headerName: 'Top Terms', field: 'topTerms',
        headerAlignDirection: AlignDirection.left,
        cellAlignDirection: AlignDirection.left,
        hide: hide, automationId: ''
    }],
    [SummaryColumnType.clusterDocuments, (hide: boolean) => <GenericColDef>{
        headerName: 'Documents', field: 'documentCount',
        headerAlignDirection: AlignDirection.right,
        cellAlignDirection: AlignDirection.right,
        hide: hide, width: 90, suppressSizeToFit: true,
        automationId: ''
    }],
    [SummaryColumnType.clusterTags, (hide: boolean) => <GenericColDef>{
        headerName: 'Tags', field: 'tagList',
        headerAlignDirection: AlignDirection.center,
        cellTemplateType: CellTemplateType.tagList,
        minWidth: 90, maxWidth: 90, width: 90,
        suppressSizeToFit: true,
        cellAlignDirection: AlignDirection.center,
        hide: hide, automationId: ''
    }],
    [SummaryColumnType.savedSearchName, (hide: boolean) => <GenericColDef>{
        headerName: 'Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        sortBy: SortByFilter.NAME,
        lockVisible: true,
        hide: hide
    }],
    [SummaryColumnType.searchTask, (hide: boolean) => <GenericColDef>{
        headerName: 'Search Task', field: 'searchTask', automationId: '',
        valueGetter: (params: ValueGetterParams) => (params && params.data && params.data.searchTask) ? params.data.searchTask.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '',
        hide: hide
    }],
    [SummaryColumnType.createdBy, (hide: boolean) => <GenericColDef>{
        headerName: 'Created by', field: 'creator', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.workflowCreatedBy, (hide: boolean) => <GenericColDef>{
        headerName: 'Created by', field: 'createdBy', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.name, (hide: boolean) => <GenericColDef>{
        headerName: 'Name', field: 'name', automationId: '',
        cellTemplateType: CellTemplateType.linkIconLabel,
        lockVisible: true,
        hide: hide
    }],
    [SummaryColumnType.locked, (hide: boolean) => <GenericColDef>{
        headerName: 'Locked',
        field: 'lockedBy',
        automationId: '',
        lockVisible: true,
        cellTemplateType: CellTemplateType.lock,
        headerAlignDirection: AlignDirection.center,
        cellAlignDirection: AlignDirection.center,
        width: 70, suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.modifiedBy, (hide: boolean) => <GenericColDef>{
        headerName: 'Modified By', field: 'modifiedBy', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.dateLastExecuted, (hide: boolean) => <GenericColDef>{
        headerName: 'Date Last Run',
        field: 'lastExecutedOn',
        automationId: '',
        headerAlignDirection: AlignDirection.right,
        cellTemplateType: CellTemplateType.date,
        cellAlignDirection: AlignDirection.right,
        width: 147,
        suppressSizeToFit: true,
        hide: hide
    }],
    [SummaryColumnType.lastExecutedBy, (hide: boolean) => <GenericColDef>{
        headerName: 'Last Run By', field: 'lastExecutedBy', automationId: '',
        hide: hide
    }],
    [SummaryColumnType.mediaIDName, (hide: boolean) => <GenericColDef>{
        headerName: 'MediaID Name', field: 'name', automationId: '', hide: hide,
        cellTemplateType: CellTemplateType.linkIconLabel, sortBy: SortByFilter.NAME,
        lockVisible: true, filterType: FilterTemplateType.freeform, suppressMovable: true,
        serviceTierFilter: true
    }],
    [SummaryColumnType.batchViewName, (hide: boolean) => <GenericColDef>{
        headerName: 'Batch Name', field: 'name', automationId: '', hide: hide,
        cellTemplateType: CellTemplateType.linkIconLabel, sortBy: SortByFilter.NAME,
        lockVisible: true, filterType: FilterTemplateType.freeform, suppressMovable: true,
        serviceTierFilter: true
    }]
]);

export const SummaryColumnInfo: ReadonlyMap<EntityType, Array<GenericColDef>> = new Map<EntityType, Array<GenericColDef>>([
    [EntityType.import, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.dataSetName)(false),
        allColumnDefs.get(SummaryColumnType.shared)(false),
        allColumnDefs.get(SummaryColumnType.representationType)(false),
        allColumnDefs.get(SummaryColumnType.lastUpdateTimestamp)(false),
        allColumnDefs.get(SummaryColumnType.inCaseData)(false),
        allColumnDefs.get(SummaryColumnType.connectorName)(false),
        allColumnDefs.get(SummaryColumnType.dataAreaPaths)(false),
        allColumnDefs.get(SummaryColumnType.originatingProject)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.batchName)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.export, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.exportName)(false),
        allColumnDefs.get(SummaryColumnType.description)(true),
        allColumnDefs.get(SummaryColumnType.exportState)(false),
        allColumnDefs.get(SummaryColumnType.exportedBy)(false),
        allColumnDefs.get(SummaryColumnType.createdOn)(false),
        allColumnDefs.get(SummaryColumnType.documents)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.custodian, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.custodianName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.priorityDisplay)(false),
        allColumnDefs.get(SummaryColumnType.documentsSortable)(false),
        allColumnDefs.get(SummaryColumnType.dateCreatedSortable)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.mediaID, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.mediaIDName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.documentsSortable)(false),
        allColumnDefs.get(SummaryColumnType.dateCreatedSortable)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.batch, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.batchViewName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.documentsSortable)(false),
        allColumnDefs.get(SummaryColumnType.dateCreatedSortable)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.folder, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.folderName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.documents)(false),
        allColumnDefs.get(SummaryColumnType.createdOn)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.tagView, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.tagName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.documents)(false),
        allColumnDefs.get(SummaryColumnType.createdOn)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.savedSearch, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.savedSearchName)(false),
        allColumnDefs.get(SummaryColumnType.searchTask)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.createdBy)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.searchResult, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.checkbox)(false),
        allColumnDefs.get(SummaryColumnType.searchName)(false),
        allColumnDefs.get(SummaryColumnType.results)(false),
        allColumnDefs.get(SummaryColumnType.owner)(false),
        allColumnDefs.get(SummaryColumnType.started)(false),
        allColumnDefs.get(SummaryColumnType.finished)(false),
        allColumnDefs.get(SummaryColumnType.task)(false),
        allColumnDefs.get(SummaryColumnType.duration)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.profile, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.profileName)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.createdOn)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.profileHistory, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.user)(false),
        allColumnDefs.get(SummaryColumnType.dateModified)(false),
        allColumnDefs.get(SummaryColumnType.profileComment)(false)
    )],
    [EntityType.comparison, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.comparisonName)(false),
        allColumnDefs.get(SummaryColumnType.documentSetA)(false),
        allColumnDefs.get(SummaryColumnType.documentSetB)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.cluster, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.clusterId)(false),
        allColumnDefs.get(SummaryColumnType.clusterName)(true),
        allColumnDefs.get(SummaryColumnType.clusteredTopTerms)(false),
        allColumnDefs.get(SummaryColumnType.clusterDocuments)(false),
        allColumnDefs.get(SummaryColumnType.clusterTags)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )],
    [EntityType.workFlow, new Array<GenericColDef>(
        allColumnDefs.get(SummaryColumnType.name)(false),
        allColumnDefs.get(SummaryColumnType.locked)(false),
        allColumnDefs.get(SummaryColumnType.description)(false),
        allColumnDefs.get(SummaryColumnType.createdOn)(true),
        allColumnDefs.get(SummaryColumnType.workflowCreatedBy)(true),
        allColumnDefs.get(SummaryColumnType.dateModified)(false),
        allColumnDefs.get(SummaryColumnType.modifiedBy)(false),
        allColumnDefs.get(SummaryColumnType.dateLastExecuted)(false),
        allColumnDefs.get(SummaryColumnType.lastExecutedBy)(false),
        allColumnDefs.get(SummaryColumnType.contextMenu)(false)
    )]
]);
